import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../components/Toast';

// ── Particle Starfield ────────────────────────────────────────────────────
function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.3 + 0.05,
      opacity: Math.random() * 0.7 + 0.1,
      twinkle: Math.random() * Math.PI * 2,
    }));

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.twinkle += 0.02;
        const opacity = s.opacity * (0.6 + 0.4 * Math.sin(s.twinkle));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(129, 140, 248, ${opacity})`;
        ctx.fill();
        s.y -= s.speed;
        if (s.y < -5) { s.y = canvas.height + 5; s.x = Math.random() * canvas.width; }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} id="starfield-canvas" className="fixed inset-0 pointer-events-none z-0" />;
}

// OTP modal removed — email verification is no longer required.
// Signup navigates directly to /onboarding.

// ── Floating Input ─────────────────────────────────────────────────────────
function FloatingInput({ id, label, type = 'text', value, onChange, error, success, rightEl, ...rest }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  const actualType = isPassword ? (show ? 'text' : 'password') : type;

  return (
    <div className="relative">
      <div className="relative">
        <input
          id={id}
          type={actualType}
          value={value}
          onChange={onChange}
          placeholder=" "
          className={`input-glass pr-10 pt-5 pb-2 text-sm ${error ? 'border-red-500/60 animate-shake' : success ? 'border-green-500/40' : ''}`}
          style={{ animationPlayState: error ? 'running' : 'paused' }}
          {...rest}
        />
        <label
          htmlFor={id}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm pointer-events-none transition-all duration-200 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:text-xs peer-focus:text-indigo-400"
          style={{
            top: value ? '8px' : '50%',
            transform: value ? 'none' : 'translateY(-50%)',
            fontSize: value ? '10px' : '14px',
            color: value ? '#818cf8' : undefined,
          }}
        >
          {label}
        </label>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isPassword && (
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="text-white/30 hover:text-white/60 transition-colors"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
          {success && !isPassword && (
            <CheckCircle size={16} className="text-green-400 animate-fade-in" />
          )}
        </div>
      </div>
      {error && <p className="text-red-400 text-xs mt-1 ml-1">{error}</p>}
    </div>
  );
}



// ── Main Login Page ────────────────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signIn, signUp, isLoading, isAuthenticated, user } = useAuthStore();

  const [tab, setTab] = useState('signin'); // 'signin' | 'signup'

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.groupId) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Sign In state
  const [siEmail, setSiEmail] = useState('');
  const [siPass, setSiPass] = useState('');
  const [siErrors, setSiErrors] = useState({});

  // Sign Up state
  const [suName, setSuName] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPass, setSuPass] = useState('');
  const [suConfirm, setSuConfirm] = useState('');
  const [suErrors, setSuErrors] = useState({});

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSignIn = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!validateEmail(siEmail)) errs.email = 'Enter a valid email';
    if (siPass.length < 6) errs.password = 'Password must be at least 6 characters';
    if (Object.keys(errs).length) { setSiErrors(errs); return; }
    setSiErrors({});

    const result = await signIn(siEmail, siPass);
    if (result.success) {
      toast.success('Welcome back to StudyLeague!');
      const currentUser = useAuthStore.getState().user;
      if (currentUser?.role === 'admin') {
        navigate('/admin');
      } else if (currentUser?.groupId) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    } else {
      setSiErrors({ submit: result.error || 'Sign in failed' });
      toast.error('Sign in failed. Please try again.');
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!suName.trim()) errs.name = 'Name is required';
    if (!validateEmail(suEmail)) errs.email = 'Enter a valid email';
    if (suPass.length < 8) errs.password = 'Password must be at least 8 characters';
    if (suPass !== suConfirm) errs.confirm = 'Passwords do not match';
    if (Object.keys(errs).length) { setSuErrors(errs); return; }
    setSuErrors({});

    // Patch 1: No OTP — navigate directly to onboarding on success
    const result = await signUp(suName, suEmail, suPass);
    if (result.success) {
      toast.success('Account created! Let\'s set up your profile.');
      navigate('/onboarding');
    } else {
      toast.error(result.error || 'Sign up failed. Please try again.');
    }
  };

  const tabs = [
    { key: 'signin', label: 'Sign In' },
    { key: 'signup', label: 'Sign Up' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #040812 0%, #090e1a 50%, #0d1426 100%)' }}>
      <Starfield />

      {/* Background glow orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #6366f1, transparent)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #f59e0b, transparent)' }} />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 animate-float"
            style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)', boxShadow: '0 0 40px rgba(99,102,241,0.5)' }}>
            <span className="text-3xl">📖</span>
          </div>
          <h1 className="heading text-4xl font-bold text-white mb-1">StudyLeague</h1>
          <p className="text-white/40 text-sm italic">Study Together, Rise Together</p>
        </div>

        <div className="glass-strong rounded-2xl overflow-hidden">
          {/* Tabs */}
          <div className="relative flex border-b border-white/10">
            {tabs.map((t, i) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 py-4 text-sm font-semibold transition-colors duration-200 ${
                  tab === t.key ? 'text-white' : 'text-white/40 hover:text-white/60'
                }`}
              >
                {t.label}
              </button>
            ))}
            {/* Sliding underline */}
            <div
              className="tab-slider"
              style={{
                left: tab === 'signin' ? '0%' : '50%',
                width: '50%',
              }}
            />
          </div>

          <div className="p-8">
            {tab === 'signin' ? (
              <form onSubmit={handleSignIn} className="space-y-4">
                <FloatingInput
                  id="si-email"
                  label="Email address"
                  type="email"
                  value={siEmail}
                  onChange={e => setSiEmail(e.target.value)}
                  error={siErrors.email}
                  success={validateEmail(siEmail)}
                />
                <FloatingInput
                  id="si-pass"
                  label="Password"
                  type="password"
                  value={siPass}
                  onChange={e => setSiPass(e.target.value)}
                  error={siErrors.password}
                />

                {/* Patch 2: No email service — replaced Forgot Password link with static guidance */}
                <p className="text-xs text-white/40 text-right">
                  Forgot your password? Contact your group leader to reset it.
                </p>

                {siErrors.submit && (
                  <p className="text-red-400 text-sm text-center animate-fade-in">{siErrors.submit}</p>
                )}

                <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70">
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <>Enter the League <ArrowRight size={16} /></>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleSignUp} className="space-y-4">
                <FloatingInput
                  id="su-name"
                  label="Full name"
                  value={suName}
                  onChange={e => setSuName(e.target.value)}
                  error={suErrors.name}
                  success={suName.trim().length > 1}
                />
                <FloatingInput
                  id="su-email"
                  label="Email address"
                  type="email"
                  value={suEmail}
                  onChange={e => setSuEmail(e.target.value)}
                  error={suErrors.email}
                  success={validateEmail(suEmail)}
                />
                <FloatingInput
                  id="su-pass"
                  label="Password"
                  type="password"
                  value={suPass}
                  onChange={e => setSuPass(e.target.value)}
                  error={suErrors.password}
                  success={suPass.length >= 8}
                />
                <FloatingInput
                  id="su-confirm"
                  label="Confirm password"
                  type="password"
                  value={suConfirm}
                  onChange={e => setSuConfirm(e.target.value)}
                  error={suErrors.confirm}
                  success={suConfirm && suPass === suConfirm}
                />

                <button type="submit" disabled={isLoading} className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-70">
                  {isLoading ? 'Creating Account...' : <>Join the League <ArrowRight size={16} /></>}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-white/20 text-xs mt-6">
          By joining, you agree to our Terms of Service &amp; Privacy Policy.
        </p>
      </div>
    </div>
  );
}
